import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ChevronDown, LogOut, LayoutDashboard, PenSquare, Inbox, Facebook, Instagram } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/LanguageContext";
import { SOCIAL_LINKS } from "@/lib/socials";

// Official Midemide Logistics lockup — full wordmark + STREAMLINING LOGISTICS tagline (309x90).
// Served locally from /app/frontend/public/ so it can't fail on CDN/CORS/cert issues.
const LOGO_URL = "/midemide-logo.png";

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(() => (typeof window !== "undefined" ? window.scrollY > 12 : false));
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavigate();

  const NAV = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/services", label: t("nav.services"), key: "services" },
    { to: "/value-proposition", label: t("nav.valueProposition"), key: "value-prop" },
    { to: "/track", label: t("nav.track"), key: "track" },
    { to: "/careers", label: t("nav.careers"), key: "careers" },
    { to: "/blog", label: t("nav.blog"), key: "blog" },
    { to: "/about", label: t("nav.about"), key: "about" },
    { to: "/contact", label: t("nav.contact"), key: "contact" },
  ];

  const LangToggle = ({ mobile = false }) => (
    <div
      data-testid="lang-toggle"
      className={`inline-flex items-center border ${mobile ? "border-white/20" : "border-white/15"} rounded-sm overflow-hidden text-[11px] font-mono tracking-wider`}
    >
      <button
        type="button"
        data-testid="lang-en"
        onClick={() => setLang("en")}
        className={`px-2.5 py-1.5 transition-colors ${lang === "en" ? "bg-[#D9633F] text-white" : "text-white/60 hover:text-white"}`}
      >EN</button>
      <button
        type="button"
        data-testid="lang-fr"
        onClick={() => setLang("fr")}
        className={`px-2.5 py-1.5 transition-colors ${lang === "fr" ? "bg-[#D9633F] text-white" : "text-white/60 hover:text-white"}`}
      >FR</button>
    </div>
  );

  useEffect(() => {
    // Passive + rAF-throttled scroll listener — eliminates forced reflows and stays off the main thread.
    let ticking = false;
    let lastY = window.scrollY;
    const onScroll = () => {
      lastY = window.scrollY;
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(() => {
          setScrolled(lastY > 12);
          ticking = false;
        });
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/80 backdrop-blur-2xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-mdgx flex items-center justify-between gap-2 py-2 sm:py-3 md:py-4">
        <Link to="/" data-testid="header-logo-link" className="flex items-center group min-w-0 shrink-0">
          {/* Wide lockup: M icon + "Midemide Logistics" wordmark + "STREAMLINING LOGISTICS" slogan — must always render together (never cropped) */}
          <img
            src={LOGO_URL}
            alt="Midemide Logistics — Streamlining Logistics"
            width="309"
            height="90"
            decoding="async"
            fetchPriority="high"
            className="h-10 sm:h-11 md:h-12 lg:h-12 xl:h-14 w-auto object-contain transition-transform group-hover:scale-[1.03] shrink-0"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.key}-link`}
              className={({ isActive }) =>
                `px-2.5 xl:px-3 py-2 text-[13px] xl:text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[#D9633F]" : "text-white/80 hover:text-white"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {/* Social icons */}
          <div className="flex items-center gap-1.5">
            <a
              data-testid="header-social-facebook"
              href={SOCIAL_LINKS.facebook.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${SOCIAL_LINKS.facebook.label} — ${SOCIAL_LINKS.facebook.handle}`}
              className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-[#D9633F] transition-colors"
            >
              <Facebook size={15} />
            </a>
            <a
              data-testid="header-social-instagram"
              href={SOCIAL_LINKS.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${SOCIAL_LINKS.instagram.label} — ${SOCIAL_LINKS.instagram.handle}`}
              className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-[#D9633F] transition-colors"
            >
              <Instagram size={15} />
            </a>
          </div>
          <span className="block w-px h-5 bg-white/10" />
          <LangToggle />
          {user ? (
            <div className="relative">
              <button
                data-testid="user-menu-button"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-white/30 rounded-sm text-sm"
              >
                <span className="w-7 h-7 rounded-full bg-[#D9633F] text-white text-xs font-bold flex items-center justify-center font-display">
                  {(user.name || user.email)[0].toUpperCase()}
                </span>
                <span className="text-white/90 max-w-[120px] truncate">{user.name || user.email}</span>
                <ChevronDown size={14} className="text-white/60" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                  <button
                    data-testid="dashboard-menu-item"
                    onClick={() => { setMenuOpen(false); nav("/dashboard"); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/90 hover:bg-white/5 hover:text-[#D9633F]"
                  >
                    <LayoutDashboard size={16} /> {t("nav.dashboard")}
                  </button>
                  {user.role === "admin" && (
                    <>
                      <button
                        data-testid="admin-inbox-menu-item"
                        onClick={() => { setMenuOpen(false); nav("/admin/inbox"); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/90 hover:bg-white/5 hover:text-[#D9633F] border-t border-white/5"
                      >
                        <Inbox size={16} /> {t("nav.adminInbox")}
                      </button>
                      <button
                        data-testid="admin-blog-menu-item"
                        onClick={() => { setMenuOpen(false); nav("/admin/blog"); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/90 hover:bg-white/5 hover:text-[#D9633F] border-t border-white/5"
                      >
                        <PenSquare size={16} /> {t("nav.adminBlog")}
                      </button>
                    </>
                  )}
                  <button
                    data-testid="logout-menu-item"
                    onClick={async () => { setMenuOpen(false); await logout(); nav("/"); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/90 hover:bg-white/5 hover:text-[#D9633F] border-t border-white/5"
                  >
                    <LogOut size={16} /> {t("nav.logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" data-testid="login-link" className="text-sm text-white/80 hover:text-white px-3 py-2">
              {t("nav.login")}
            </Link>
          )}
          <Link to="/quote" data-testid="header-quote-cta" className="btn-sharp btn-primary">
            {t("nav.requestQuote")}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-black border-t border-white/10">
          <div className="container-mdgx py-6 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.key}`}
                className={({ isActive }) =>
                  `py-3 text-base ${isActive ? "text-[#D9633F]" : "text-white/90"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-3 pb-1">
              <span className="text-xs text-white/40 uppercase tracking-[0.2em]">{t("nav.language")}</span>
              <LangToggle mobile />
            </div>
            <div className="flex items-center gap-3 pt-1 pb-2">
              <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Social</span>
              <a
                data-testid="mobile-social-facebook"
                href={SOCIAL_LINKS.facebook.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={SOCIAL_LINKS.facebook.label}
                onClick={() => setOpen(false)}
                className="w-9 h-9 border border-white/15 hover:border-[#D9633F] hover:text-[#D9633F] text-white/85 flex items-center justify-center transition-colors"
              >
                <Facebook size={14} />
              </a>
              <a
                data-testid="mobile-social-instagram"
                href={SOCIAL_LINKS.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={SOCIAL_LINKS.instagram.label}
                onClick={() => setOpen(false)}
                className="w-9 h-9 border border-white/15 hover:border-[#D9633F] hover:text-[#D9633F] text-white/85 flex items-center justify-center transition-colors"
              >
                <Instagram size={14} />
              </a>
            </div>
            <div className="flex flex-col gap-3 pt-4 border-t border-white/10 mt-2">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="text-white/90 py-2">{t("nav.dashboard")}</Link>
                  <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="text-left text-white/90 py-2">{t("nav.logout")}</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="text-white/90 py-2">{t("nav.login")}</Link>
              )}
              <Link to="/quote" onClick={() => setOpen(false)} className="btn-sharp btn-primary">{t("nav.requestQuote")}</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
